// Mirrors the backend's CurrencyEnum. The API serialises it as a number, so a
// property priced in dollars comes back as `currency: 2`, not "USD".
export enum CurrencyEnum {
  NGN = 1,
  USD = 2,
  EUR = 3,
  GBP = 4,
  JPY = 5,
  // Canadian dollar. The backend spells it CND rather than the ISO "CAD".
  CND = 6,
}

export const DEFAULT_CURRENCY = CurrencyEnum.NGN;

const CURRENCY_SYMBOLS: Record<CurrencyEnum, string> = {
  [CurrencyEnum.NGN]: "₦",
  [CurrencyEnum.USD]: "$",
  [CurrencyEnum.EUR]: "€",
  [CurrencyEnum.GBP]: "£",
  [CurrencyEnum.JPY]: "¥",
  [CurrencyEnum.CND]: "CA$",
};

// String codes still appear on the request side (PropertyRequest.currency is
// "NGN" | "USD" | ...), so accept those as well as the numeric form.
const CODE_TO_CURRENCY: Record<string, CurrencyEnum> = {
  NGN: CurrencyEnum.NGN,
  USD: CurrencyEnum.USD,
  EUR: CurrencyEnum.EUR,
  GBP: CurrencyEnum.GBP,
  JPY: CurrencyEnum.JPY,
  CND: CurrencyEnum.CND,
  CAD: CurrencyEnum.CND,
};

const isKnownCurrency = (value: number): value is CurrencyEnum =>
  Object.prototype.hasOwnProperty.call(CURRENCY_SYMBOLS, value);

// Codes for showing to users. Canadian dollar is shown as the recognisable
// "CAD" even though the backend enum spells it CND.
const CURRENCY_CODES: Record<CurrencyEnum, string> = {
  [CurrencyEnum.NGN]: "NGN",
  [CurrencyEnum.USD]: "USD",
  [CurrencyEnum.EUR]: "EUR",
  [CurrencyEnum.GBP]: "GBP",
  [CurrencyEnum.JPY]: "JPY",
  [CurrencyEnum.CND]: "CAD",
};

// Strict parse: the matching CurrencyEnum, or undefined when the value is
// missing or unrecognised. The public helpers below each decide for
// themselves what "unrecognised" should mean.
function parseCurrency(currency: unknown): CurrencyEnum | undefined {
  if (typeof currency === "number") {
    return isKnownCurrency(currency) ? currency : undefined;
  }

  if (typeof currency === "string") {
    const trimmed = currency.trim();

    if (/^\d+$/.test(trimmed)) {
      const numeric = Number(trimmed);
      return isKnownCurrency(numeric) ? numeric : undefined;
    }

    return CODE_TO_CURRENCY[trimmed.toUpperCase()];
  }

  return undefined;
}

// Normalises whatever the API or a form hands us into a CurrencyEnum.
// Anything missing or unrecognised falls back to Naira.
export function toCurrency(currency: unknown): CurrencyEnum {
  return parseCurrency(currency) ?? DEFAULT_CURRENCY;
}

// Display code such as "USD". Undefined for anything unrecognised rather than
// guessing, so callers can fall back to generic wording — otherwise an unknown
// currency would read as "priced in NGN" on a property that isn't Naira.
export function getCurrencyCode(currency: unknown): string | undefined {
  const parsed = parseCurrency(currency);
  return parsed === undefined ? undefined : CURRENCY_CODES[parsed];
}

export function getCurrencySymbol(currency: unknown): string {
  return CURRENCY_SYMBOLS[toCurrency(currency)];
}

// Whether a property can be bought from the wallet. Wallet balances are held
// in Naira, so only Naira-priced properties qualify.
//
// Deliberately stricter than toCurrency(): that one falls back to Naira for
// anything unrecognised, which is right for picking a display symbol but
// wrong for a payment gate. Here only an explicit NGN counts. The one
// exception is a property with no currency at all — those were listed before
// multi-currency support existed and are Naira.
export function isNairaCurrency(currency: unknown): boolean {
  if (currency === undefined || currency === null || currency === "") {
    return true;
  }

  return parseCurrency(currency) === CurrencyEnum.NGN;
}

export const FOREIGN_CURRENCY_WALLET_MESSAGE =
  "Properties priced in a foreign currency can't be purchased with your wallet balance.";
